import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Send, CheckCircle, User, Users, Home, HelpCircle } from 'lucide-react';
import { API } from '../App';

const PRAYER_TYPES = [
  { id: 'health', label: 'Health & healing', icon: '🏥' },
  { id: 'family', label: 'Family & relationships', icon: '👨‍👩‍👧‍👦' },
  { id: 'financial', label: 'Financial stress & employment', icon: '💼' },
  { id: 'grief', label: 'Grief & loss', icon: '💔' },
  { id: 'guidance', label: 'Guidance & major life decisions', icon: '🧭' },
  { id: 'addiction', label: 'Addiction & destructive behaviors', icon: '⛓️' },
  { id: 'mental', label: 'Mental & emotional distress', icon: '🧠' },
  { id: 'protection', label: 'Protection & safety', icon: '🛡️' },
  { id: 'spiritual', label: 'Spiritual struggles', icon: '✝️' },
  { id: 'intercessory', label: 'Intercessory prayers for others', icon: '🙏' }
];

const REQUEST_FOR_OPTIONS = [
  { value: 'self', label: 'Myself', icon: <User size={20} /> },
  { value: 'friend', label: 'A Friend', icon: <Users size={20} /> },
  { value: 'family', label: 'Family Member', icon: <Home size={20} /> },
  { value: 'community', label: 'Community Member', icon: <Users size={20} /> },
  { value: 'other', label: 'Other', icon: <HelpCircle size={20} /> }
];

function PrayerRequest() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    prayer_types: [],
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    request_for: '',
    request_for_name: '',
    details: ''
  });

  const handlePrayerTypeToggle = (typeLabel) => {
    setForm(prev => ({
      ...prev,
      prayer_types: prev.prayer_types.includes(typeLabel)
        ? prev.prayer_types.filter(t => t !== typeLabel)
        : [...prev.prayer_types, typeLabel]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.prayer_types.length === 0) {
      toast.error('Please select at least one prayer category');
      return;
    }
    
    if (!form.request_for) {
      toast.error('Please select who this prayer is for');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/prayer-requests`, form);
      setSubmitted(true);
      toast.success('Prayer request submitted successfully');
    } catch (error) {
      toast.error('Failed to submit prayer request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#fffbeb' }}>
        {/* Hero */}
        <div className="section-padding bg-blue-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Heart size={64} className="mx-auto mb-6" />
            <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Prayer Request Received
            </h1>
          </div>
        </div>

        <div className="section-padding">
          <div className="max-w-2xl mx-auto text-center">
            <div className="card p-12">
              <CheckCircle size={80} className="mx-auto mb-6 text-green-500" />
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#1e3a8a' }}>
                Thank You for Sharing
              </h2>
              <p className="text-xl text-slate-700 mb-6">
                Your prayer request has been submitted to our prayer team. We are honored to lift you up in prayer.
              </p>
              <p className="text-lg text-slate-600 mb-8">
                &quot;Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God.&quot; - Philippians 4:6
              </p>
              <button 
                onClick={() => {
                  setSubmitted(false);
                  setForm({
                    prayer_types: [],
                    requester_name: '',
                    requester_email: '',
                    requester_phone: '',
                    request_for: '',
                    request_for_name: '',
                    details: ''
                  });
                }}
                className="btn-primary"
              >
                Submit Another Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fffbeb' }}>
      {/* Hero */}
      <div className="section-padding bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart size={64} className="mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="prayer-title">
            Prayer Requests
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Share your prayer needs with our church family. We believe in the power of prayer and would be honored to pray for you.
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Prayer Types */}
            <div className="card p-8">
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>
                What would you like prayer for? *
              </h2>
              <p className="text-slate-600 mb-6">Select all that apply</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {PRAYER_TYPES.map((type) => (
                  <label
                    key={type.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      form.prayer_types.includes(type.label)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-amber-200 hover:border-amber-400'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.prayer_types.includes(type.label)}
                      onChange={() => handlePrayerTypeToggle(type.label)}
                      className="w-5 h-5 rounded"
                      data-testid={`prayer-type-${type.id}`}
                    />
                    <span className="text-2xl">{type.icon}</span>
                    <span className="text-lg font-medium" style={{ color: '#1e3a8a' }}>{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Who is this for */}
            <div className="card p-8">
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>
                Who is this prayer for? *
              </h2>
              <p className="text-slate-600 mb-6">Select one</p>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {REQUEST_FOR_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                      form.request_for === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-amber-200 hover:border-amber-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="request_for"
                      value={option.value}
                      checked={form.request_for === option.value}
                      onChange={(e) => setForm({ ...form, request_for: e.target.value })}
                      className="sr-only"
                      data-testid={`request-for-${option.value}`}
                    />
                    <span className="text-blue-900">{option.icon}</span>
                    <span className="text-base font-medium" style={{ color: '#1e3a8a' }}>{option.label}</span>
                  </label>
                ))}
              </div>

              {form.request_for && form.request_for !== 'self' && (
                <div className="mt-6">
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                    Name of person you&apos;re requesting prayer for (optional)
                  </label>
                  <input
                    type="text"
                    value={form.request_for_name}
                    onChange={(e) => setForm({ ...form, request_for_name: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    placeholder="Their first name or initials"
                    data-testid="request-for-name"
                  />
                </div>
              )}
            </div>

            {/* Your Information */}
            <div className="card p-8">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
                Your Information
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.requester_name}
                    onChange={(e) => setForm({ ...form, requester_name: e.target.value })}
                    className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    placeholder="Enter your name"
                    data-testid="requester-name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={form.requester_email}
                      onChange={(e) => setForm({ ...form, requester_email: e.target.value })}
                      className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg focus:border-blue-500 focus:outline-none"
                      placeholder="your@email.com"
                      data-testid="requester-email"
                    />
                    <p className="text-sm text-slate-500 mt-1">We&apos;ll only contact you regarding your prayer request</p>
                  </div>
                  <div>
                    <label className="block text-lg font-semibold mb-2" style={{ color: '#1e3a8a' }}>
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      value={form.requester_phone}
                      onChange={(e) => setForm({ ...form, requester_phone: e.target.value })}
                      className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg focus:border-blue-500 focus:outline-none"
                      placeholder="(541) 555-0000"
                      data-testid="requester-phone"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="card p-8">
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#1e3a8a' }}>
                Additional Details (optional)
              </h2>
              <p className="text-slate-600 mb-6">Share any specific details about your prayer request. This information will only be seen by our prayer team.</p>
              
              <textarea
                rows={5}
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                className="w-full px-4 py-3 text-lg border-2 border-amber-200 rounded-lg focus:border-blue-500 focus:outline-none"
                placeholder="Share more about your prayer request..."
                data-testid="prayer-details"
              />
            </div>

            {/* Privacy Notice */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-bold mb-2" style={{ color: '#1e3a8a' }}>🔒 Privacy & Confidentiality</h3>
              <p className="text-slate-700">
                Your prayer request will be shared only with our dedicated prayer team and church leadership. 
                All information is kept confidential and treated with care and respect.
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full text-xl py-4"
              data-testid="submit-prayer"
            >
              {submitting ? (
                'Submitting...'
              ) : (
                <>
                  <Send size={24} className="inline mr-2" />
                  Submit Prayer Request
                </>
              )}
            </button>
          </form>

          {/* Scripture Quote */}
          <div className="mt-12 text-center">
            <blockquote className="text-xl italic text-slate-700 max-w-2xl mx-auto">
              &quot;Therefore I tell you, whatever you ask for in prayer, believe that you have received it, and it will be yours.&quot;
            </blockquote>
            <p className="text-lg font-semibold mt-2" style={{ color: '#1e3a8a' }}>— Mark 11:24</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrayerRequest;
